import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { Layout } from './components/Layout';
import { RequireAuth } from './components/RequireAuth';

// ── Chunk-load-error recovery ────────────────────────────────────────────
// When a deploy ships, the lazy-loaded chunk filenames change (Vite hashes
// them). Any tab still holding the OLD index.html will try to fetch a
// chunk by its old hash; that 404 falls back to index.html through
// CloudFront, the browser sees text/html where it expected JS, and React
// surfaces "Failed to fetch dynamically imported module".
//
// Recovery: detect that specific error, reload the page ONCE (with a
// query-bust so the new index.html is fetched, not a cached one). Once
// reloaded, lazy chunks resolve against the new hashes and life continues.
//
// We guard with a sessionStorage flag so we don't loop on a real failure
// (e.g. the chunk genuinely doesn't exist any more — gives the user a
// chance to see the error and try again rather than reload-storm).
const RELOAD_FLAG = 'mfj_chunk_reload_done';

function installChunkErrorRecovery() {
  if (typeof window === 'undefined') return;
  const handler = (event: PromiseRejectionEvent | ErrorEvent) => {
    const reason = 'reason' in event ? event.reason : (event as ErrorEvent).error;
    const message = String(reason?.message ?? reason ?? '');
    const isChunkError =
      /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(message);
    if (!isChunkError) return;
    if (sessionStorage.getItem(RELOAD_FLAG)) {
      // Already tried once; don't loop. Let the error surface so the user
      // sees the spinner stop and can decide to refresh manually.
      console.error('[chunk-recovery] chunk load failed AFTER reload — not retrying', message);
      return;
    }
    sessionStorage.setItem(RELOAD_FLAG, '1');
    console.warn('[chunk-recovery] stale lazy chunk; reloading to pick up new bundle');
    // Cache-bust the index.html load so we definitely fetch a fresh one,
    // not a 5-min-cached copy.
    const url = new URL(window.location.href);
    url.searchParams.set('mfj_r', Date.now().toString(36));
    window.location.replace(url.toString());
  };
  window.addEventListener('unhandledrejection', handler);
  window.addEventListener('error', handler as EventListener);
}

// Clear the flag on a clean load so the next stale-chunk event can trigger
// recovery again. Runs once at module init.
if (typeof window !== 'undefined') {
  // If the URL has the cache-bust param, we just came out of recovery —
  // strip it and reset the flag.
  const url = new URL(window.location.href);
  if (url.searchParams.has('mfj_r')) {
    sessionStorage.removeItem(RELOAD_FLAG);
    url.searchParams.delete('mfj_r');
    window.history.replaceState({}, '', url.toString());
  }
  installChunkErrorRecovery();
}

// Eagerly-loaded routes — the first-visit set (auth + dashboard + kids
// browsing). Bundling them keeps the initial navigation snappy.
import { Welcome } from './routes/Welcome';
import { Signup } from './routes/Signup';
import { Verify } from './routes/Verify';
import { Login } from './routes/Login';
import { ForgotPassword } from './routes/ForgotPassword';
import { ForgotConfirm } from './routes/ForgotConfirm';
import { Dashboard } from './routes/Dashboard';
import { Kids } from './routes/Kids';
import { KidAdd } from './routes/KidAdd';
import { KidDetail } from './routes/KidDetail';
import { KidEdit } from './routes/KidEdit';
import { MatchDetail } from './routes/MatchDetail';
import { MatchAdd } from './routes/MatchAdd';
import { MatchEdit } from './routes/MatchEdit';

// Lazy-loaded routes — heavier deps (recharts, the full component gallery,
// upgrade page). These code-split out of the main bundle to reduce
// first-paint payload. Each route lives in its own chunk and downloads only
// when the user navigates there.
const Stats = lazy(() => import('./routes/Stats').then((m) => ({ default: m.Stats })));
const Achievements = lazy(() => import('./routes/Achievements').then((m) => ({ default: m.Achievements })));
const Upgrade = lazy(() => import('./routes/Upgrade').then((m) => ({ default: m.Upgrade })));
const DesignPreview = lazy(() => import('./routes/DesignPreview').then((m) => ({ default: m.DesignPreview })));
const Settings = lazy(() => import('./routes/Settings').then((m) => ({ default: m.Settings })));
const InviteAccept = lazy(() => import('./routes/InviteAccept').then((m) => ({ default: m.InviteAccept })));
const KidPrint = lazy(() => import('./routes/KidPrint').then((m) => ({ default: m.KidPrint })));
const SeasonRecap = lazy(() => import('./routes/SeasonRecap').then((m) => ({ default: m.SeasonRecap })));
const MatchRecord = lazy(() => import('./routes/MatchRecord').then((m) => ({ default: m.MatchRecord })));

function NotFound() {
  return (
    <div>
      <h1>Not found</h1>
      <p style={{ color: 'var(--muted)' }}>That page doesn't exist.</p>
    </div>
  );
}

function Boot({ children }: { children: React.ReactNode }) {
  const { hydrating } = useAuth();
  if (hydrating) return null;
  return <>{children}</>;
}

/**
 * Fallback rendered while a lazy route loads. Quiet, brand-consistent —
 * matches the rest of the app's "Loading…" treatments.
 */
function RouteFallback() {
  return (
    <p style={{ color: 'var(--muted)', padding: 'var(--space-base) 0' }}>Loading…</p>
  );
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Boot>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route element={<Layout />}>
                <Route index element={<Welcome />} />
                <Route path="signup" element={<Signup />} />
                <Route path="verify" element={<Verify />} />
                <Route path="login" element={<Login />} />
                <Route path="forgot" element={<ForgotPassword />} />
                <Route path="forgot-confirm" element={<ForgotConfirm />} />
                <Route path="dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                <Route path="kids" element={<RequireAuth><Kids /></RequireAuth>} />
                <Route path="kids/new" element={<RequireAuth><KidAdd /></RequireAuth>} />
                <Route path="kids/:kidId" element={<RequireAuth><KidDetail /></RequireAuth>} />
                <Route path="kids/:kidId/edit" element={<RequireAuth><KidEdit /></RequireAuth>} />
                <Route path="kids/:kidId/matches/new" element={<RequireAuth><MatchAdd /></RequireAuth>} />
                {/* Stage 2 of the two-stage match flow — events + result + note.
                    See web/src/routes/MatchRecord.tsx and the matched
                    MatchAdd setup form. */}
                <Route path="kids/:kidId/matches/:matchId/record" element={<RequireAuth><MatchRecord /></RequireAuth>} />
                <Route path="kids/:kidId/matches/:matchId" element={<RequireAuth><MatchDetail /></RequireAuth>} />
                <Route path="kids/:kidId/matches/:matchId/edit" element={<RequireAuth><MatchEdit /></RequireAuth>} />
                <Route path="kids/:kidId/achievements" element={<RequireAuth><Achievements /></RequireAuth>} />
                <Route path="kids/:kidId/stats" element={<RequireAuth><Stats /></RequireAuth>} />
                <Route path="kids/:kidId/season-recap/:season" element={<RequireAuth><SeasonRecap /></RequireAuth>} />
                <Route path="upgrade" element={<RequireAuth><Upgrade /></RequireAuth>} />
                <Route path="settings" element={<RequireAuth><Settings /></RequireAuth>} />
                <Route path="invite/:token" element={<InviteAccept />} />
                {/* Internal design-system gallery — open route, no auth. Not linked from any nav. */}
                <Route path="design-preview" element={<DesignPreview />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              {/* Print route renders OUTSIDE Layout so the magazine recap
                  doesn't carry the app header/footer onto the PDF. Auth
                  required — the page itself plan-gates to Reports tier. */}
              <Route path="kids/:kidId/print" element={<RequireAuth><KidPrint /></RequireAuth>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Boot>
      </BrowserRouter>
    </AuthProvider>
  );
}
